export enum ScheduleType {
    revenue = "revenue",
    expense = "prepaid_expense",
    fixedAsset = "fixed_asset",
}

export enum RevenueSubMode {
    deferred = "deferred",
    accrued = "accrued",
}

export enum ExpenseSubMode {
    prepaid = "prepaid",
    accrued = "accrued",
}

// TODO(PRO-12970): drop the 'deferred' value from schedule_template_expensesubmode_enum once the data migration has deployed everywhere.
export const RETIRED_EXPENSE_SUB_MODE_DEFERRED = "deferred";

export enum ScheduleTemplateStatus {
    active = "active",
    archived = "archived",
    deleted = "deleted",
}

export enum ScheduleApplicationStatus {
    draft = "draft",
    active = "active",
    paused = "paused",
    unwound = "unwound",
    stopped = "stopped",
    accelerated = "accelerated",
    released = "released",
    completed = "completed",
    disposed = "disposed",
    deleted = "deleted",
}

/** Statuses that free the source for a new schedule and no longer guard it. */
export const SCHEDULE_APPLICATION_RETIRED_STATUSES = [
    ScheduleApplicationStatus.unwound,
    ScheduleApplicationStatus.disposed,
    ScheduleApplicationStatus.deleted,
] as const;

/**
 * Statuses that never conflict with a schedule on the other side of a match:
 * retired ones, and a rule-suggested draft, which is not a schedule yet.
 * Confirming the draft runs the conflict check against the counterpart.
 */
export const SCHEDULE_APPLICATION_NON_CONFLICTING_STATUSES = [
    ...SCHEDULE_APPLICATION_RETIRED_STATUSES,
    ScheduleApplicationStatus.draft,
] as const;

export enum ScheduleLifecycleErrorCode {
    periodLocked = "period_locked",
    alreadyPosted = "already_posted",
}

/**
 * Status filter values for the schedules list. Every value but `modified`
 * maps onto one stored status; `modified` is an active schedule that has
 * been changed since it was applied.
 */
export enum ScheduleLifecycleFilterStatus {
    active = "active",
    modified = "modified",
    paused = "paused",
    stopped = "stopped",
    completed = "completed",
    unwound = "unwound",
}

export enum ScheduleBillingStatus {
    unbilled = "unbilled",
    partiallyBilled = "partially_billed",
    billed = "billed",
}

export enum ScheduleCreationJeStatus {
    pending = "pending",
    posted = "posted",
    notRequired = "not_required",
}

/**
 * Which invoice lines a schedule covers. Applications created before tax and
 * deposit lines were excluded keep `allLines`: every line, sized on the total.
 */
export enum ScheduleInvoiceLineScope {
    allLines = "all_lines",
    itemRevenue = "item_revenue",
}

export enum RecognitionMethod {
    straightLine = "straight_line",
    dailyProRata = "daily_pro_rata",
    pointInTime = "point_in_time",
    milestone = "milestone",
}

export function isTermRecognitionMethod(method: RecognitionMethod): boolean {
    return (
        method === RecognitionMethod.straightLine ||
        method === RecognitionMethod.dailyProRata
    );
}

export function isDailyProRataTermUnit(unit: ScheduleTermUnit): boolean {
    return unit === ScheduleTermUnit.months || unit === ScheduleTermUnit.years;
}

export function hasTermRecognitionComponent(
    components: ReadonlyArray<{ method: RecognitionMethod }>,
): boolean {
    return components.some((component) =>
        isTermRecognitionMethod(component.method),
    );
}

export function hasDailyProRataComponent(
    components: ReadonlyArray<{ method: RecognitionMethod }>,
): boolean {
    return components.some(
        (component) => component.method === RecognitionMethod.dailyProRata,
    );
}

export function isDailyProRataTermUnitAllowed(options: {
    components: ReadonlyArray<{ method: RecognitionMethod }>;
    termUnit: ScheduleTermUnit;
}): boolean {
    return (
        !hasDailyProRataComponent(options.components) ||
        isDailyProRataTermUnit(options.termUnit)
    );
}

export enum RecognitionAnchor {
    invoiceDate = "invoice_date",
    billDate = "bill_date",
    transactionDate = "transaction_date",
    termStart = "term_start",
    specificDate = "specific_date",
}

export enum ScheduleTermUnit {
    days = "days",
    weeks = "weeks",
    months = "months",
    years = "years",
}

export enum ScheduleMilestoneAnchor {
    sourceDate = "source_date",
    specificDate = "specific_date",
}

export enum ScheduleMilestoneOffsetUnit {
    days = "days",
    months = "months",
    years = "years",
}

export enum ScheduleJeSubtype {
    addition = "addition",
    recognized = "recognized",
    disposal = "disposal",
    reclassification = "reclassification",
    capitalAddition = "capital_addition",
    reversal = "reversal",
    catchUp = "catch_up",
    /** Clears accrued revenue when a manual accrued schedule is marked as billed. */
    settlement = "settlement",
}

export enum ScheduleApplicationPeriodStatus {
    pending = "pending",
    posted = "posted",
    voided = "voided",
}

/**
 * What a period row stands for. Every schedule journal entry pair has exactly
 * one row: `ladder` is a planned recognition month, `reversal` cancels the row
 * it points at with `reversesPeriodId`, and `lifecycle` is a one-off entry a
 * verb posted (opening adjustment, catch-up, stop settlement).
 */
export enum ScheduleApplicationPeriodRole {
    ladder = "ladder",
    lifecycle = "lifecycle",
    reversal = "reversal",
}

export enum ScheduleSourceType {
    transaction = "transaction",
    invoice = "invoice",
    bill = "bill",
    manual = "manual",
}

/**
 * One undo verb. `delete` replaces the old unwind/remove pair: the mechanism
 * (reverse an earned entry vs hard-delete a future one) is decided per journal
 * entry by its date, not by the user or by the application's state.
 */
export enum ScheduleLifecycleVerb {
    delete = "delete",
    pause = "pause",
    resume = "resume",
    cancel = "cancel",
    change = "change",
}

export type ScheduleStatusTransitionVerb =
    | ScheduleLifecycleVerb.cancel
    | ScheduleLifecycleVerb.pause
    | ScheduleLifecycleVerb.resume;

/**
 * What `delete` does with the journal entries it clears. `reverse` keeps the
 * ledger's history: an earned entry gets a same-month reversal and only future
 * ones are removed. `remove` hard-deletes every entry; the audit log is then
 * the only record.
 */
export enum ScheduleDeleteJournalEntryMode {
    reverse = "reverse",
    remove = "remove",
}

/**
 * What a change does with the entries already earned. `reverse` and `remove`
 * tear the whole ladder down as delete does; `catchUp` keeps every earned
 * entry and posts one net holding/recognition adjustment in the first open
 * month, so locked months never need reopening.
 */
export enum ScheduleChangeJournalEntryMode {
    reverse = "reverse",
    remove = "remove",
    catchUp = "catch_up",
}

/**
 * What a stop does with the balance still on the holding account. Leaving it
 * is the absence of a settlement, not a third kind: no entry posts.
 */
export enum ScheduleStopSettlementKind {
    accelerate = "accelerate",
    reclass = "reclass",
}

/** What saving an edited invoice did to a schedule on it. */
export enum ScheduleSourceEditOutcome {
    changed = "changed",
    removed = "removed",
}

export enum ScheduleLifecycleTreatment {
    reverseAll = "reverse_all",
    voidPending = "void_pending",
    none = "none",
    catchUpRespread = "catch_up_respread",
}

/** The user's answer for one invoice whose links would carry two live schedules. */
export enum ScheduleMatchResolutionKind {
    keepInvoice = "keep_invoice",
    keepTransactions = "keep_transactions",
    unmatch = "unmatch",
    moveToDocument = "move_to_document",
}
